import { MemoryHistory, formatBytes, sortedMemoryRows } from "./memory-report.js";

// Installed with the view so the native Linux bundle uses precisely the same
// layout without adding another host asset or a privileged resource route.
const styles = `
.memory-view { min-height:0; overflow:auto; padding:8px; font:12px/1.45 system-ui,sans-serif; color:inherit; }
.memory-view [hidden] { display:none!important; }
.memory-toolbar,.memory-selection { display:flex; flex-wrap:wrap; align-items:center; gap:6px; margin-bottom:6px; }
.memory-view button,.memory-view input,.memory-view select { font:inherit; max-width:100%; }
.memory-view button { padding:3px 8px; }
.memory-view button:disabled { opacity:.55; }
.memory-view label { display:flex; flex-wrap:wrap; align-items:center; gap:4px; }
.memory-view input { min-width:8em; width:14em; }
.memory-view select { min-width:8em; max-width:22em; }
.memory-view p { margin:5px 0; }
.memory-view .memory-state { font-weight:600; }
.memory-view .memory-warning { color:#a64a00; }
.memory-view .memory-scroll { overflow:auto; }
.memory-view table { width:100%; border-collapse:collapse; font:inherit; }
.memory-view th,.memory-view td { border-bottom:1px solid #8885; padding:4px 8px; text-align:right; vertical-align:top; white-space:nowrap; }
.memory-view th:first-child,.memory-view td:first-child { text-align:left; white-space:normal; overflow-wrap:anywhere; min-width:12em; }
.memory-view summary { cursor:pointer; }
.memory-view details p { white-space:normal; font-weight:normal; margin:3px 0; max-width:44em; }
.memory-view .memory-growth { font-weight:700; }
.memory-view .memory-unavailable { opacity:.7; }
.memory-view .memory-coverage { margin-top:8px; }
.memory-view .memory-help { opacity:.85; }
`;

export function setupMemoryView({ root, capture, exportReport, status = () => {} }) {
  const doc = root.ownerDocument;
  if (!doc.getElementById("memory-view-style")) {
    const style = doc.createElement("style");
    style.id = "memory-view-style";
    style.textContent = styles;
    doc.head.append(style);
  }
  root.classList.add("memory-view");
  root.setAttribute("aria-label", "Memory reports");
  const history = new MemoryHistory();
  let session = { active: false, paused: false, supported: false, pending: false, safe: true };
  let comparing = false, message = "";
  let restoreCaptureFocus = false;
  const el = (tag, content = "", className = "") => {
    const node = doc.createElement(tag);
    node.textContent = content;
    if (className) node.className = className;
    return node;
  };
  const reportError = (error) => { message = error.message || String(error); render(); status(message); };
  const action = (label, callback, id) => {
    const button = el("button", label);
    button.type = "button";
    button.dataset.memoryAction = id;
    button.addEventListener("click", async () => { try { await callback(); } catch (error) { reportError(error); } });
    return button;
  };
  const toolbar = el("div", "", "memory-toolbar");
  const take = action("Take Snapshot", async () => {
    if (!session.active || !session.paused || !session.supported || !session.safe || session.pending) return;
    restoreCaptureFocus = doc.activeElement === take;
    message = "";
    session.pending = true;
    render();
    try { await capture(); } catch (error) { session.pending = false; throw error; }
  }, "capture");
  const baseline = action("Set Baseline", () => { history.setBaseline(); message = "Baseline selected. Peaks still cover the context's lifetime."; render(); }, "baseline");
  const compare = action("Compare", () => { comparing = !comparing; render(); }, "compare");
  compare.setAttribute("aria-pressed", "false");
  const save = action("Export JSON", async () => {
    // Export the selected capture and its baseline, without a runtime request.
    const json = history.exportJson();
    await exportReport(json, `memory-snapshot-${history.selected.snapshot.sequence}.json`);
  }, "export");
  toolbar.append(take, baseline, compare, save);
  const state = el("p", "", "memory-state");
  state.setAttribute("role", "status");
  state.setAttribute("aria-live", "polite");
  const selection = el("div", "", "memory-selection");
  const snapshots = el("select");
  snapshots.setAttribute("aria-label", "Selected memory snapshot");
  snapshots.addEventListener("change", () => { history.select(snapshots.value); message = ""; render(); });
  const rename = el("input");
  rename.setAttribute("aria-label", "Snapshot name");
  rename.maxLength = 256;
  const renameAction = () => { history.rename(history.selectedId, rename.value); render(); };
  rename.addEventListener("keydown", (event) => { if (event.key === "Enter") { event.preventDefault(); try { renameAction(); } catch (error) { reportError(error); } } });
  const renameButton = action("Rename", renameAction, "rename");
  const kind = el("select");
  kind.setAttribute("aria-label", "Memory detail kind");
  for (const [value, name] of [["categories", "Categories"], ["types", "Types"]]) {
    const option = el("option", name); option.value = value; kind.append(option);
  }
  const filter = el("input");
  filter.type = "search";
  filter.placeholder = "Filter names";
  filter.setAttribute("aria-label", "Filter memory rows");
  const sort = el("select");
  sort.setAttribute("aria-label", "Sort memory rows");
  for (const [value, name] of [["growth", "Byte growth"], ["count", "Active count"], ["bytes", "Accounted bytes"], ["name", "Name"]]) {
    const option = el("option", name); option.value = value; sort.append(option);
  }
  for (const input of [kind, sort]) input.addEventListener("change", () => renderRows());
  filter.addEventListener("input", () => renderRows());
  selection.append(snapshots, rename, renameButton, kind, filter, sort);
  const summary = el("p");
  const notice = el("p", "", "memory-warning");
  const scroll = el("div", "", "memory-scroll");
  scroll.tabIndex = 0;
  scroll.setAttribute("role", "region");
  scroll.setAttribute("aria-label", "Memory allocation table");
  const table = el("table");
  const head = el("thead"), heading = el("tr");
  for (const title of ["Category or Type", "Active count", "Accounted bytes", "Count change", "Byte change"]) {
    const th = el("th", title); th.scope = "col"; heading.append(th);
  }
  head.append(heading);
  const body = el("tbody");
  table.append(head, body); scroll.append(table);
  const empty = el("p");
  const help = el("p", "Runtime accounted bytes cover the calling context, not process memory. Type pools can keep reusable slots after active instances return to baseline. Growth is not proof of a leak. Peaks are lifetime maxima; their difference is not an interval peak.", "memory-help");
  const coverage = el("details", "", "memory-coverage");
  coverage.append(el("summary", "Supplemental metrics and coverage"));
  const coverageBody = el("div"); coverage.append(coverageBody);
  root.replaceChildren(toolbar, state, selection, summary, notice, scroll, empty, help, coverage);

  const signed = (value, bytes = false) => value === null || value === undefined ? "Unavailable" :
    `${BigInt(value) > 0n ? "+" : ""}${bytes ? formatBytes(value) : value}`;
  const details = (row) => {
    const node = el("details"); node.append(el("summary", row.name));
    const facts = [];
    for (const [field, label] of [["deletedRetainedCount", "Deleted, retained by references"], ["reusableSlots", "Reusable slots"],
      ["allocatedSlots", "Allocated slots"], ["entries", "Entries"], ["capacity", "Capacity"], ["logicalBytes", "Logical bytes"],
      ["retiredSlots", "Retired handle slots"]]) {
      if (row.available && row[field] !== undefined) facts.push(`${label}: ${row[field]}`);
    }
    if (!row.available) facts.push("This metric is unavailable; no measured count or byte value is shown.");
    if (row.available && row.peakBytes !== undefined) facts.push(`Lifetime peak: ${formatBytes(row.peakBytes)} (${row.peakBytes} B)`);
    if (row.available && row.change) facts.push(`Baseline peak: ${formatBytes(row.change.baselinePeak)}; current peak: ${formatBytes(row.change.currentPeak)}`);
    if (row.scope) facts.push(`Scope: ${row.scope}; basis: ${row.basis}; coverage: ${row.coverageRevision}`);
    if (row.nameAvailable === false) facts.push("Qualified Type name unavailable; identity is local to this run.");
    if (row.nameTruncated) facts.push("Qualified Type name was shortened to fit the report; the identity remains distinct.");
    if (row.change?.reason) facts.push(row.change.reason);
    for (const fact of facts) node.append(el("p", fact));
    return node;
  };
  function renderRows() {
    body.replaceChildren();
    const selected = history.selected;
    if (!selected) { empty.textContent = "No snapshot yet. Pause a debug run, then choose Take Snapshot."; return; }
    const snapshot = selected.snapshot, comparison = comparing ? history.comparison() : null;
    const values = sortedMemoryRows(snapshot, comparison, { kind: kind.value, filter: filter.value, sort: sort.value });
    for (const row of values) {
      const tr = el("tr");
      if (!row.available) tr.classList.add("memory-unavailable");
      if (BigInt(row.change?.byteChange || "0") > 0n || BigInt(row.change?.countChange || "0") > 0n) tr.classList.add("memory-growth");
      const name = el("td"); name.append(details(row)); tr.append(name);
      tr.append(el("td", row.available ? row.activeCount : "Unavailable"));
      const bytes = el("td", row.available ? formatBytes(row.accountedBytes) : "Unavailable");
      bytes.title = row.available ? `${row.accountedBytes} bytes` : "Unavailable"; tr.append(bytes);
      tr.append(el("td", comparing ? signed(row.change?.countChange) : "—"));
      const delta = el("td", comparing ? signed(row.change?.byteChange, true) : "—");
      if (row.change?.byteChange != null) delta.title = `${row.change.byteChange} bytes`;
      tr.append(delta); body.append(tr);
    }
    const allChanges = [...(comparison?.categories || []), ...(comparison?.types || [])];
    const unchanged = comparison?.compatible && comparison.complete && allChanges.every((row) =>
      !row.reason && row.countChange === "0" && row.byteChange === "0");
    empty.textContent = !values.length ? (filter.value ? "No matching rows." : "No Type allocations were captured.") :
      unchanged ? "No change since baseline." : comparison && !comparison.compatible ? comparison.reason : "";
    empty.hidden = !empty.textContent;
  }
  function render() {
    const selected = history.selected;
    take.disabled = !session.active || !session.paused || !session.supported || !session.safe || session.pending;
    take.title = !session.active ? "Start a debug run to capture memory." : !session.supported ?
      "This runtime does not support memory reports." : !session.safe ? "Memory capture is unavailable at a runtime error." :
      !session.paused ? "Pause the program before capturing memory." : "Capture on the paused program's owning thread.";
    baseline.disabled = save.disabled = renameButton.disabled = !selected;
    compare.disabled = !selected || !history.baseline;
    compare.setAttribute("aria-pressed", String(comparing));
    state.textContent = message || (session.pending ? "Capturing memory…" : !session.active ?
      (selected ? "Program ended. Captured reports remain available until the next run." : "No debug session. Start a debug run to capture memory.") :
      !session.supported ? "Memory reporting is unavailable in this runtime." : !session.safe ?
        "Memory capture is unavailable at a runtime error. Previous reports remain available." : !session.paused ?
        "Pause the program before taking a snapshot." : "Program paused. Ready to take a memory snapshot.");
    snapshots.replaceChildren();
    for (const entry of history.snapshots) {
      const option = el("option", `${entry.label}${entry.id === history.baselineId ? " (baseline)" : ""}`);
      option.value = entry.id; option.selected = entry.id === history.selectedId; snapshots.append(option);
    }
    rename.value = selected?.label || "";
    selection.hidden = scroll.hidden = summary.hidden = notice.hidden = help.hidden = coverage.hidden = !selected;
    empty.hidden = false;
    if (selected) {
      const snapshot = selected.snapshot;
      summary.textContent = `${selected.label} — Runtime accounted bytes: ${snapshot.available ? formatBytes(snapshot.totals.accountedBytes) : "Unavailable"}; lifetime peak: ${snapshot.available ? formatBytes(snapshot.totals.peakBytes) : "Unavailable"}. Context ${snapshot.contextId} (${snapshot.domain}). Baseline: ${history.baseline?.label || "none"}.`;
      notice.textContent = !snapshot.available ? snapshot.error || "Runtime accounting unavailable." : !snapshot.complete ?
        `Incomplete Type detail: ${snapshot.omittedTypeRows} rows omitted. Missing rows cannot be treated as freed.` : "";
      notice.hidden = !notice.textContent;
      coverageBody.replaceChildren();
      const supplementalChanges = new Map((comparing ? history.comparison()?.supplemental || [] : [])
        .map((change) => [change.id, change]));
      for (const metric of snapshot.supplemental) {
        const item = el("details");
        item.append(el("summary", `${metric.name}: ${metric.available ? (metric.unit === "bytes" ? formatBytes(metric.current) : `${metric.current} ${metric.unit}`) : "Unavailable"}`));
        item.append(el("p", `Scope: ${metric.scope}; basis: ${metric.basis}; coverage: ${metric.coverageRevision}. ${metric.relationship}.`));
        const age = metric.sampleTimestamp !== "0" && BigInt(snapshot.timestamp) >= BigInt(metric.sampleTimestamp) ?
          `${BigInt(snapshot.timestamp) - BigInt(metric.sampleTimestamp)} ms before capture` : "Unavailable";
        item.append(el("p", `Sample timestamp: ${metric.sampleTimestamp === "0" ? "Unavailable" : metric.sampleTimestamp}; age: ${age}; frame: ${metric.sampleFrame === "0" ? "Unavailable" : metric.sampleFrame}. Peak: ${metric.peakAvailable ? (metric.unit === "bytes" ? formatBytes(metric.peak) : `${metric.peak} ${metric.unit}`) : "Unavailable"}.`));
        if (metric.limitation) item.append(el("p", metric.limitation));
        if (metric.limitationTruncated) item.append(el("p", "Coverage explanation shortened to fit the report."));
        const change = supplementalChanges.get(metric.id);
        if (change?.reason) item.append(el("p", change.reason));
        else if (change) item.append(el("p", `Change since baseline: ${signed(change.valueChange, metric.unit === "bytes")}${metric.unit === "bytes" ? "" : ` ${metric.unit}`}. Baseline peak: ${change.baselinePeak === null ? "Unavailable" : metric.unit === "bytes" ? formatBytes(change.baselinePeak) : `${change.baselinePeak} ${metric.unit}`}; current peak: ${change.currentPeak === null ? "Unavailable" : metric.unit === "bytes" ? formatBytes(change.currentPeak) : `${change.currentPeak} ${metric.unit}`}.`));
        coverageBody.append(item);
      }
    }
    renderRows();
  }
  render();
  return {
    history,
    setSession(value) {
      if (["active", "paused", "safe"].some((key) => key in value && value[key] !== session[key])) message = "";
      session = { ...session, ...value }; render();
    },
    accept(snapshot) {
      const result = history.add(snapshot);
      session.pending = false;
      comparing = history.snapshots.length > 1;
      message = result.evicted ? `${result.evicted.label} was removed to keep eight snapshots. The baseline is pinned.` : "";
      render();
      if (restoreCaptureFocus && doc.activeElement === doc.body && !take.disabled) take.focus();
      restoreCaptureFocus = false;
      if (message) status(message);
      return result;
    },
    error(value) {
      session.pending = false; reportError(value);
      if (restoreCaptureFocus && doc.activeElement === doc.body && !take.disabled) take.focus();
      restoreCaptureFocus = false;
    },
    reset() { history.clear(); comparing = false; message = ""; session.pending = false; render(); },
  };
}
